var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import Set from '../../shim/Set';
import Map from '../../shim/Map';
import { create, invalidator, diffProperty, destroy } from '../vdom';
import icache from './icache';
import { auto } from '../diff';
function generateSynthIdString({ requestId, orderId }) {
    return `${requestId}/${orderId}`;
}
class RawCache {
    constructor() {
        this._subscriberCounter = 1;
        this._rawCache = new Map();
        this._syntheticIdToIdMap = new Map();
        this._idToSyntheticIdMap = new Map();
        this._syntheticIdToSubscriberMap = new Map();
        this._subscriberMap = new Map();
    }
    subscribe(syntheticIds, invalidator) {
        const subscriberId = `${this._subscriberCounter++}`;
        syntheticIds.forEach((syntheticId) => {
            const synthId = generateSynthIdString(syntheticId);
            let subscribers = this._syntheticIdToSubscriberMap.get(synthId);
            if (!subscribers) {
                subscribers = new Set();
            }
            subscribers.add(subscriberId);
            this._syntheticIdToSubscriberMap.set(synthId, subscribers);
        });
        this._subscriberMap.set(subscriberId, {
            syntheticIds,
            invalidator,
            refs: new Set(syntheticIds.map(generateSynthIdString))
        });
    }
    notify(syntheticId) {
        const synthId = generateSynthIdString(syntheticId);
        const subscriberIds = this._syntheticIdToSubscriberMap.get(synthId);
        if (subscriberIds) {
            [...subscriberIds].forEach((subscriberId) => {
                const subscriber = this._subscriberMap.get(subscriberId);
                if (subscriber) {
                    subscriber.refs.delete(synthId);
                    if (subscriber.refs.size === 0) {
                        subscriber.invalidator();
                        this._subscriberMap.delete(subscriberId);
                    }
                    const subscribers = this._syntheticIdToSubscriberMap.get(synthId);
                    subscribers.delete(subscriber);
                    this._syntheticIdToSubscriberMap.set(synthId, subscribers);
                }
            });
        }
    }
    get(syntheticId) {
        const synthId = generateSynthIdString(syntheticId);
        const idDetails = this._syntheticIdToIdMap.get(synthId);
        if (idDetails) {
            if (idDetails.status === 'resolved') {
                return this._rawCache.get(idDetails.id);
            }
            return {
                status: idDetails.status,
                mtime: Date.now(),
                value: undefined,
                stale: idDetails.stale
            };
        }
    }
    delete(id) {
        this._idToSyntheticIdMap.delete(id);
        this._rawCache.delete(id);
    }
    invalidate() {
        this._rawCache.forEach((value, key) => {
            this._rawCache.set(key, Object.assign({}, value, { stale: true }));
        });
        this._syntheticIdToIdMap.forEach((value, key) => {
            this._syntheticIdToIdMap.set(key, Object.assign({}, value, { stale: true }));
        });
    }
    addSyntheticId(syntheticId, status = 'pending') {
        this._syntheticIdToIdMap.set(generateSynthIdString(syntheticId), { id: null, status });
    }
    getSyntheticIds(id) {
        const ids = this._idToSyntheticIdMap.get(id);
        if (ids) {
            return [...ids.values()];
        }
        return [];
    }
    orphan(syntheticId) {
        const synthId = generateSynthIdString(syntheticId);
        this.notify(syntheticId);
        this._syntheticIdToIdMap.set(synthId, { id: null, status: 'orphaned' });
    }
    set(syntheticId, item, idKey) {
        const synthId = generateSynthIdString(syntheticId);
        const id = item.value[idKey];
        this._syntheticIdToIdMap.set(synthId, { id, status: 'resolved' });
        const syntheticIds = this._idToSyntheticIdMap.get(id) || new Map();
        syntheticIds.set(syntheticId.requestId, syntheticId);
        this._idToSyntheticIdMap.set(id, syntheticIds);
        this._rawCache.set(id, Object.assign({}, item, { stale: false }));
        this.notify(syntheticId);
    }
}
export function defaultFilter(query, item, type = 'contains') {
    const queryKeys = Object.keys(query);
    for (let i = 0; i < queryKeys.length; i++) {
        const queryKey = queryKeys[i];
        const value = query[queryKeys[i]];
        if (value) {
            const itemValue = item[queryKey];
            let result = true;
            if (typeof itemValue === 'string' && typeof value === 'string' && type !== 'exact') {
                if (type === 'contains') {
                    result = item[queryKey].toLowerCase().indexOf(value.toLowerCase()) !== -1;
                }
                else {
                    result = item[queryKey].toLowerCase().indexOf(value.toLowerCase()) === 0;
                }
            }
            else {
                result = value === item[queryKey];
            }
            if (!result) {
                return false;
            }
        }
    }
    return true;
}
export function createResourceTemplate(templateOrWidget, template) {
    template = template || templateOrWidget;
    if (typeof template === 'function') {
        return (templateOptions) => {
            return {
                template: {
                    template,
                    templateOptions
                }
            };
        };
    }
    if (typeof template === 'string') {
        return createResourceTemplate(({ data }) => ({
            idKey: template,
            read: (request, { put }) => {
                const { query, offset } = request;
                const filteredData = Object.keys(query).length
                    ? data.filter((item) => item && defaultFilter(query, item, 'contains'))
                    : data;
                put({ data: filteredData.slice(offset), total: filteredData.length }, request);
            }
        }));
    }
    return {
        template: {
            template: () => template,
            templateOptions: {}
        }
    };
}
const factory = create({
    invalidator,
    destroy,
    diffProperty,
    icache
}).properties();
// The template cache, this holds the RawCache instance and request inprogress flags
const templateCacheMap = new Map();
const optionsCacheMap = new Map();
// The reverse look up for the owning id, this is so that widgets passed a resource options can add their invalidator to subscribers
const optionsSetterToOwnerIdMap = new Map();
let optionsId = 0;
function isTemplateWrapper(value) {
    return Boolean(value && value.template && typeof value.template === 'function');
}
function isResourceWrapper(value) {
    return Boolean(value && value.template && typeof value.template.template === 'function');
}
function transformQuery(query, transformConfig) {
    if (Array.isArray(transformConfig)) {
        return [...transformConfig].reverse().reduce((query, config) => {
            return transformQuery(query, config);
        }, Object.assign({}, query));
    }
    const queryKeys = Object.keys(query);
    let transformedQuery = {};
    for (let i = 0; i < queryKeys.length; i++) {
        const queryKey = queryKeys[i];
        transformedQuery[transformConfig[queryKey] || queryKey] = query[queryKey];
    }
    return transformedQuery;
}
function transformData(item, transformConfig) {
    if (!transformConfig || !item) {
        return item;
    }
    if (Array.isArray(transformConfig)) {
        return transformConfig.reduce((transformedItem, config) => {
            return transformData(transformedItem, config);
        }, Object.assign({}, item));
    }
    let transformedItem = {};
    let sourceKeys = [];
    Object.keys(transformConfig).forEach((key) => {
        const sourceKey = transformConfig[key];
        transformedItem = Object.assign({}, transformedItem, { [key]: item[sourceKey] });
        sourceKeys.push(sourceKey);
    });
    Object.keys(item)
        .filter((key) => sourceKeys.indexOf(key) === -1)
        .forEach((key) => {
        transformedItem = Object.assign({}, transformedItem, { [key]: item[key] });
    });
    return transformedItem;
}
function getOrCreateResourceCaches(template) {
    if (template === undefined) {
        throw new Error('Resource template cannot be undefined.');
    }
    if (!isTemplateWrapper(template)) {
        template = template.template;
    }
    const templateCache = templateCacheMap.get(template.template) || new Map();
    templateCacheMap.set(template.template, templateCache);
    const cacheKey = JSON.stringify(template.templateOptions);
    let caches = templateCache.get(cacheKey);
    if (!caches) {
        caches = {
            raw: new RawCache(),
            requestCache: new Map(),
            instance: template.template(template.templateOptions),
            invalidators: new Set()
        };
        templateCache.set(cacheKey, caches);
    }
    return caches;
}
const middleware = factory(({ id, properties, middleware: { invalidator, destroy, diffProperty, icache } }) => {
    const uuid = icache.getOrSet('uuid', `${++optionsId}`);
    const destroyFuncs = [];
    diffProperty('resource', properties, ({ resource: currResource }, { resource: nextResource }) => {
        if (!nextResource) {
            return undefined;
        }
        if (!currResource ||
            currResource.options !== nextResource.options ||
            currResource.template !== nextResource.template) {
            invalidator();
        }
        if (nextResource.options) {
            const id = optionsSetterToOwnerIdMap.get(nextResource.options);
            if (id) {
                const optionsWrapper = optionsCacheMap.get(id);
                if (optionsWrapper && !optionsWrapper.subscribers.has(invalidator)) {
                    optionsWrapper.subscribers.add(invalidator);
                    destroyFuncs.push(() => {
                        optionsWrapper.subscribers.delete(invalidator);
                    });
                }
            }
        }
        if (!currResource && !nextResource.template) {
            const _a = nextResource, { template: _template, options, idKey } = _a, rest = __rest(_a, ["template", "options", "idKey"]);
            const template = createResourceTemplate(idKey);
            nextResource.template = template(rest).template;
        }
        else if (currResource && !nextResource.template) {
            const _b = nextResource, { template: _template, options, idKey } = _b, rest = __rest(_b, ["template", "options", "idKey"]);
            nextResource.template = {
                template: currResource.template.template,
                templateOptions: rest
            };
        }
        return nextResource;
    });
    destroy(() => {
        destroyFuncs.forEach((des) => des());
    });
    const resource = (options) => {
        if (!options || !options.template) {
            throw new Error('Resource cannot be undefined');
        }
        if (isTemplateWrapper(options)) {
            return {
                template: { template: Object.assign({}, options), transform: options.transform }
            };
        }
        let transform = options.transform;
        let existingTransform = options.template.transform;
        if (existingTransform) {
            if (transform) {
                existingTransform = Array.isArray(existingTransform) ? existingTransform : [existingTransform];
                transform = [...existingTransform, transform];
            }
            else {
                transform = existingTransform;
            }
        }
        if (isResourceWrapper(options)) {
            return {
                template: Object.assign({}, options, { transform }),
                options: options.options,
                transform
            };
        }
        return {
            template: Object.assign({}, options.template, { transform }),
            options: options.options,
            transform
        };
    };
    function createOptions(setter, optionsId = uuid) {
        const existingOptions = optionsCacheMap.get(optionsId);
        if (existingOptions) {
            return existingOptions.setter;
        }
        const optionsWrapper = {
            options: {},
            subscribers: new Set(),
            setter: (options) => {
                return options;
            }
        };
        optionsWrapper.subscribers.add(invalidator);
        function setOptions(newOptions) {
            if (!newOptions) {
                optionsWrapper.subscribers.add(invalidator);
                return {
                    offset: optionsWrapper.options.offset || 0,
                    size: optionsWrapper.options.size || 30,
                    query: optionsWrapper.options.query || {}
                };
            }
            const updatedOptions = setter(optionsWrapper.options, newOptions);
            if (auto(updatedOptions, optionsWrapper.options, 5).changed) {
                optionsWrapper.options = updatedOptions;
                optionsWrapper.subscribers.forEach((i) => {
                    i();
                });
            }
            return {
                offset: optionsWrapper.options.offset || 0,
                size: optionsWrapper.options.size || 30,
                query: optionsWrapper.options.query || {}
            };
        }
        setOptions({});
        destroyFuncs.push(() => {
            optionsCacheMap.delete(id);
        });
        optionsWrapper.setter = setOptions;
        optionsCacheMap.set(optionsId, optionsWrapper);
        optionsSetterToOwnerIdMap.set(setOptions, optionsId);
        destroyFuncs.push(() => optionsSetterToOwnerIdMap.delete(setOptions));
        return setOptions;
    }
    resource.createOptions = createOptions;
    resource.template = function (template) {
        const { instance, raw: cache, invalidators, requestCache } = getOrCreateResourceCaches(template);
        const apiKeys = Object.keys(instance).filter((key) => typeof instance[key] === 'function');
        let transform;
        if (isResourceWrapper(template) && template.transform) {
            transform = template.transform;
        }
        const templateApi = apiKeys.reduce((api, key) => {
            api[key] = (args) => {
                const put = (response, request) => {
                    if (Array.isArray(response)) {
                        response.forEach((item) => {
                            const id = item[instance.idKey];
                            const synthIds = cache.getSyntheticIds(id);
                            cache.invalidate();
                            synthIds.forEach((id) => {
                                cache.set(id, {
                                    value: item,
                                    status: 'resolved',
                                    mtime: Date.now()
                                }, instance.idKey);
                            });
                            invalidators.forEach((invalidator) => invalidator());
                        });
                    }
                    else {
                        const { offset, query: requestQuery, size } = request;
                        const query = transform ? transformQuery(requestQuery, transform) : requestQuery;
                        const syntheticIds = [];
                        for (let i = 0; i < offset + size - offset; i++) {
                            syntheticIds.push({ requestId: JSON.stringify(query), orderId: `${offset + i}` });
                        }
                        response.data.forEach((item, idx) => {
                            const syntheticId = syntheticIds.shift() || {
                                requestId: JSON.stringify(query),
                                orderId: `${offset + idx}`
                            };
                            cache.set(syntheticId, {
                                value: item,
                                status: 'resolved',
                                mtime: Date.now()
                            }, instance.idKey);
                        });
                        syntheticIds.forEach((id) => cache.orphan(id));
                        const requestCacheData = requestCache.get(JSON.stringify(query));
                        if (requestCacheData) {
                            requestCacheData.total = response.total;
                            requestCache.set(JSON.stringify(query), requestCacheData);
                        }
                    }
                };
                const del = (items) => {
                    cache.invalidate();
                    invalidators.forEach((invalidator) => invalidator());
                    items.forEach((item) => {
                        cache.delete(item[instance.idKey]);
                    });
                };
                const get = (request) => {
                    if (Array.isArray(request)) {
                        return request.map((id) => {
                            const [synthId] = cache.getSyntheticIds(id);
                            if (synthId) {
                                const item = cache.get(synthId);
                                if (item) {
                                    return item.value;
                                }
                            }
                        });
                    }
                    let items = [];
                    const { offset, size, query } = request;
                    const end = offset + size;
                    for (let i = 0; i < end - offset; i++) {
                        const item = cache.get({ requestId: JSON.stringify(query), orderId: `${offset + i}` });
                        if (!item || item.status === 'pending') {
                            return;
                        }
                        if (item && item.status === 'resolved') {
                            items.push(transformData(item.value, transform));
                        }
                    }
                    return items;
                };
                instance[key](args, { put, del, get });
            };
            return api;
        }, {});
        function get(request, settings = {}) {
            const caches = getOrCreateResourceCaches(template);
            const { raw: cache, requestCache, invalidators } = caches;
            if (Array.isArray(request)) {
                return request.map((id) => {
                    const [synthId] = cache.getSyntheticIds(id);
                    if (synthId) {
                        const item = cache.get(synthId);
                        if (item) {
                            return item.value;
                        }
                    }
                });
            }
            const { meta = false, read } = settings;
            if (read && !invalidators.has(invalidator)) {
                invalidators.add(invalidator);
                destroyFuncs.push(() => {
                    invalidators.delete(invalidator);
                });
            }
            let { size, offset, query } = request;
            let transform;
            if (isResourceWrapper(template) && template.transform) {
                transform = template.transform;
                query = transformQuery(query, transform);
            }
            const end = offset + size;
            const stringifiedRequest = JSON.stringify(request);
            const stringifiedQuery = JSON.stringify(query);
            let requestCacheData = requestCache.get(stringifiedQuery) || {
                inflightMap: new Map(),
                total: undefined
            };
            const inflight = requestCacheData.inflightMap.get(stringifiedRequest) || false;
            if (!settings.meta && inflight) {
                return undefined;
            }
            const syntheticIds = [];
            const orphanedIds = [];
            let incompleteIds = [];
            let shouldRead = false;
            let resetOrphans = false;
            if (!read) {
                let items = [];
                let requestStatus = 'read';
                for (let i = 0; i < end - offset; i++) {
                    const item = cache.get({ requestId: stringifiedQuery, orderId: `${offset + i}` });
                    if (meta) {
                        if (item) {
                            const status = item.status === 'resolved' ? 'read' : 'reading';
                            if (requestStatus === 'read' && status === 'reading') {
                                requestStatus = 'reading';
                            }
                            items.push({ value: transformData(item.value, transform), status });
                        }
                        else {
                            requestStatus = 'unread';
                            items.push({ value: undefined, status: 'unread' });
                        }
                    }
                    else {
                        if (item && item.status === 'resolved') {
                            items.push(transformData(item.value, transform));
                        }
                        else {
                            items.push(undefined);
                        }
                    }
                }
                if (meta) {
                    return { data: items, meta: { status: requestStatus, total: requestCacheData.total } };
                }
                return items;
            }
            let items = [];
            for (let i = 0; i < end - offset; i++) {
                const syntheticId = { requestId: stringifiedQuery, orderId: `${offset + i}` };
                const item = cache.get(syntheticId);
                syntheticIds.push(syntheticId);
                if (item) {
                    if (item.stale) {
                        incompleteIds.push(syntheticId);
                        if (item.value && item.status === 'resolved') {
                            cache.set(syntheticId, Object.assign({}, item, { stale: false }), instance.idKey);
                        }
                        else if (item.status === 'orphaned') {
                            cache.addSyntheticId(syntheticId, item.status);
                        }
                        shouldRead = true;
                        if (orphanedIds.length) {
                            resetOrphans = true;
                        }
                        items.push(item);
                    }
                    else if (item.status === 'resolved') {
                        if (orphanedIds.length) {
                            resetOrphans = true;
                            shouldRead = true;
                        }
                        items.push(item);
                    }
                    else if (item.status === 'pending') {
                        incompleteIds.push(syntheticId);
                        if (orphanedIds.length) {
                            resetOrphans = true;
                            shouldRead = true;
                        }
                        items.push(item);
                    }
                    else {
                        items.push(item);
                        orphanedIds.push(syntheticId);
                    }
                }
                else {
                    incompleteIds.push(syntheticId);
                    cache.addSyntheticId(syntheticId);
                    items.push({
                        status: 'pending',
                        mtime: Date.now(),
                        value: undefined
                    });
                    shouldRead = true;
                }
            }
            if (resetOrphans) {
                incompleteIds = [...incompleteIds, ...orphanedIds];
                orphanedIds.forEach((id) => {
                    cache.addSyntheticId(id);
                });
            }
            if (incompleteIds.length) {
                cache.subscribe(incompleteIds, () => {
                    invalidator();
                });
            }
            if (shouldRead) {
                requestCacheData.inflightMap.set(stringifiedRequest, true);
                requestCache.set(stringifiedQuery, requestCacheData);
                settings.read && settings.read({ size, offset, query });
                requestCacheData.inflightMap.set(stringifiedRequest, false);
                requestCache.set(stringifiedQuery, requestCacheData);
                if (!requestCacheData.inflightMap.get(stringifiedRequest)) {
                    items = [];
                    for (let i = 0; i < syntheticIds.length; i++) {
                        const syntheticId = syntheticIds[i];
                        const item = cache.get(syntheticId);
                        if (item) {
                            if (item.status !== 'orphaned') {
                                items.push(item);
                            }
                        }
                    }
                }
            }
            if (!resetOrphans) {
                items = items.filter((item) => item.status !== 'orphaned');
            }
            if (settings.meta) {
                let requestStatus = 'read';
                const data = items.map((item) => {
                    let status = 'read';
                    if (item.status !== 'resolved') {
                        status = 'reading';
                    }
                    if (requestStatus === 'read' && status === 'reading') {
                        requestStatus = 'reading';
                    }
                    return {
                        value: transformData(item.value, transform),
                        status
                    };
                });
                return {
                    data,
                    meta: { status: requestStatus, total: requestCacheData.total }
                };
            }
            const filteredItems = items
                .filter((item) => item.status !== 'pending')
                .map((item) => transformData(item.value, transform));
            return items.length === filteredItems.length ? filteredItems : undefined;
        }
        return {
            template: templateApi,
            createOptions,
            get
        };
    };
    return resource;
});
export function createResourceMiddleware() {
    return middleware.withType();
}
//# sourceMappingURL=resources.mjs.map